import { PartnerType } from 'legacy/lut/classes/models/PartnerType'
import { EltType } from 'legacy/lut/components/widgets/public/partner360/ELT'
import { I621Form } from 'legacy/lut/features/partner360/configuration'
import { ICommitmentData } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentData'
import { IApiCommitmentStatus } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentStatus'
import {
  CommitmentReminderActionTypeEnum,
  ICommitmentReminderActionItem,
} from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'

import type { IGeoMappingItem, IMappedGeoMappingItem } from 'partner360/components/filters'
import type { IAttentionStatusApi } from 'partner360/entities/attentionStatuses'
import type { IJBPPlanning } from 'partner360/entities/planning/model'
import { IMdfActivityApi } from 'partner360/features/mdfActivities/api/types'
import type { IProgramOfferingApi } from 'partner360/helpers/offerings/programOfferings'
import type { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import type { IPartnerFlagRequest } from 'partner360/models/partnerFlagRequests/IPartnerFlagRequest'

import {
  ICockpitCompletenessData,
  ICockpitMdfActivity,
  ICockpitMdfSales,
  ICockpitPartnerData,
  ICockpitPartnerOfferingsCatalog,
  ICockpitProgramOffering,
  IPartnerFinSobRow,
} from '../model/types'

export interface IPartnerFinSobDataApiGroup {
  PartnerId: string
  Offering: string
  PartnerFinSobData: IJBPPlanning[]
}

export interface IPartnerFlagRequestsApiGroup {
  PartnerId: string
  PartnerFlagRequests: IPartnerFlagRequest[]
}

export interface IPartnerFlagApproverApi {
  Approver: string
}

export interface IFinancialCockpitPlanningDeadline {
  PartnerType: string
  StartDate: string
  EndDate: string
}

export interface IFinancialCockpitPlanningCycle {
  PlanningType: 'jbp-sob-collecting' | 'jbp-planning' | 'jbp-planning-review'
  FYear: number
  FQuarter: 1 | 2 | 3 | 4
  SortOrder: number
  Deadlines: IFinancialCockpitPlanningDeadline[]
}

export interface ICockpitCommitmentStatus {
  PartnerId: string
  /** Older responses included this wrapper-level field; the current payload identifies the
   *  offering through each nested status' `ProgramType`. */
  Offering?: string
  CommitmentStatus: IApiCommitmentStatus[]
}

export interface ICockpitCommitmentData {
  PartnerId: string
  Offering: string
  CommitmentData: ICommitmentData[]
}

/** ProgramOfferingsJson entry with the screen and PDF defaults supplied by the cockpit API. */
export interface ICockpitProgramOfferingApi extends IProgramOfferingApi {
  ActualsValueTypeDefault?: string
  PDFActualsValueTypeDefault: string
  PDFActualsValueTypes: string
}

/** One category configuration row from `ActualsAndTargetsSettings`. Optional fields are
 *  omitted by the backend when the corresponding behavior does not apply to the category. */
export interface IFinancialCockpitActualsAndTargetsSetting {
  EntryId: number
  ProgramType: string
  Category: string
  CategoryOrder: number
  SubCategory: string
  SubCategoryOrder: number
  L3Category: string
  L3CategoryOrder: number
  ShowUnits?: 0 | 1
  ShowShipments?: 0 | 1
  ShowNotApplicable?: 0 | 1
  Colors?: string
  JBPGeoUnit?: string
  PLs: string
  HideIfEmpty?: 0 | 1
  /** Serialized category UI/value-type configuration. */
  Settings?: string
  IsContractualsAvailable?: 0 | 1
  IsSoBAvailable?: 0 | 1
  IsUnitsAvailable?: 0 | 1
  MandatoryType: number
  FYear: number
  MandatoryByFlags?: string
  UnitsMandatoryByFlags?: string
  SOBMandatoryByFlags?: string
  GlobalCategorySettings: string
  IsSoBTotalCalc?: 0 | 1
  IsOnlyQuarterlyPlanning: 0 | 1
  IsHideFinancial?: 0 | 1
  PopupComment: string
  IsMainHierarchy: 0 | 1
}

/** Fields `spJBPMDFActivities` returns and `planningCockpitGzip` does not return at all: the
 *  outcome block plus the P360 initiative link the activity form writes. This endpoint feeds
 *  read-only cockpit aggregates, not that form. */
type TCockpitMdfMissingField =
  | 'P360InitiativeId'
  | 'Outcome'
  | 'OutcomeValueType'
  | 'FunnelSize'
  | 'Leads'
  | 'ConversionRate'
  | 'Attendance'
  | 'EstimateSales'

/** Fields this endpoint **omits** where the stored procedure sends an explicit `null`. */
type TCockpitMdfOmittableField =
  | 'StrategicInitiative'
  | 'BusinessUnit'
  | 'Category'
  | 'BusinessFocus'
  | 'ApprovalDate'
  | 'KeyInitiativeId'
  | 'KeyInitiativeLinkType'

/** An MDF activity as returned inside `MDFActivities[].MDFActivities` by `planningCockpitGzip`.
 *
 *  The same entity the `mdfActivities` feature loads from `spJBPMDFActivities`, so it is derived
 *  from that feature's `IMdfActivityApi` rather than transcribed again — field names, types and
 *  their documentation stay in one place, and a field added there shows up here. Only what this
 *  payload does differently is stated:
 *  - it drops the fields in `TCockpitMdfMissingField`;
 *  - it omits the keys in `TCockpitMdfOmittableField` instead of sending `null`;
 *  - `StatusCategory` also arrives as `''`, not only omitted, on unclassified activities;
 *  - there is no `FYear` in either shape: the payload is already scoped to the requested year;
 *  - the offering, on the other hand, is not scoped away — every offering comes at once, so the
 *    activity carries its own `ProgramTypes` for this side to filter on.
 *
 *  `lib/mapCockpitMdfActivityFromApi` closes those gaps and hands the result to the same
 *  `mapMdfActivityFromApi`, so the store holds the shared domain `IMdfActivity` and every MDF
 *  calculation stays shared with the `mdfActivities` feature. */
export type ICockpitMdfActivityApi = Omit<
  IMdfActivityApi,
  TCockpitMdfMissingField | TCockpitMdfOmittableField | 'StatusCategory'
> &
  Partial<{
    [Field in TCockpitMdfOmittableField]: NonNullable<IMdfActivityApi[Field]>
  }> & {
    /** Lifecycle phase — the stored procedure's values, plus `''` for "not classified". */
    StatusCategory?: NonNullable<IMdfActivityApi['StatusCategory']> | ''
    /** Offerings the activity belongs to — plural, because its category split can spread across
     *  business units that map to different offerings. A `;`/`,`-separated list or an array,
     *  depending on how the procedure serialises it. */
    ProgramTypes?: string | string[]
  }

/** Activities of one partner, grouped by the offering they belong to. Unlike the MDF widget —
 *  which asks `spJBPMDFActivities` for one `ProgramType` and gets a list already scoped — the
 *  cockpit fetches every offering at once, so the offering travels with the data and is applied
 *  on this side. */
export interface IFinancialCockpitPartnerMdfActivities {
  PartnerId: string
  MDFActivities: ICockpitMdfActivityApi[]
}

/** `IFinancialCockpitPartnerMdfActivities` with its activities mapped to the domain model. */
export interface ICockpitPartnerMdfActivities {
  PartnerId: string
  MDFActivities: ICockpitMdfActivity[]
}

export interface ICockpitPartnerMdfSales {
  PartnerId: string
  MDFSales: ICockpitMdfSales[]
}

export enum FinancialCockpitPendingActionOwner {
  Mine = 'Mine',
  SalesManager = 'SalesManager',
  PBM = 'PBM',
}

export enum FinancialCockpitPendingActionPriority {
  Common = 1,
  Medium = 2,
  High = 3,
}

export interface IFinancialCockpitPendingAction extends ICommitmentReminderActionItem {
  ActionUser: string
  Action?: string
  ActionCategory: string
  ActionType: CommitmentReminderActionTypeEnum
  ActionSortOrder: number
  ActionPriority: FinancialCockpitPendingActionPriority
  /** `Mine` for the current user's actions; otherwise a backend-driven subordinate role. */
  ActionOwner: FinancialCockpitPendingActionOwner
}

export interface IFinancialCockpitPendingActionsOffering {
  PartnerId: string
  Offering: string
  PBMActions: IFinancialCockpitPendingAction[]
}

export enum CockpitUserType {
  Partner = 'Partner',
  Manager = 'Manager',
  PBM = 'PBM',
}

export interface IFinancialCockpitUser {
  UserType: CockpitUserType
  Id: number
  Email: string
  FullName: string
}

export interface IFinancialCockpitPartnerUsersOffering {
  PartnerId: string
  Offering: string
  Users: IFinancialCockpitUser[]
}

export interface IFinancialCockpitUserWithScope extends IFinancialCockpitUser {
  PartnerId: string
  Offering: string
}

/** The requesting user's profile and cockpit access scope. The service returns it as a
 *  one-element `UserInfo` array. */
export interface IFinancialCockpitUserInfo {
  UserId: string
  UserFullName: string
  UserFirstName: string
  UserRoleId: string
  AccessRole: string
  AccessLevel: string
  AccessLevelDetails: string
  AccessRTM: string
  AccessOfferings: string
}

/** A key initiative exactly as returned inside `KeyInitiatives[].KeyInitiatives` by
 *  `planningCockpitGzip`. Optional fields are the fields absent from at least one initiative
 *  in the captured production payload. */
export interface IFinancialCockpitKeyInitiative {
  EntryId: number
  PartnerId: string
  Created: string
  FYear: number
  /** Empty/null means an offering-independent initiative, visible under every offering. */
  ProgramType: string | null
  Description: string
  Development: string
  FocusArea?: string
  FocusAreaTranslation?: string
  Name: string
  GoalNdp?: number
  GoalNet?: number
  GoalTNet?: number
  IncrementalNdp?: number
  IncrementalNet?: number
  IncrementalTNet?: number
  IncrementalNdpLocalCurrency?: number
  IncrementalNdpUSD?: number
  IncrementalNetLocalCurrency?: number
  IncrementalNetUSD?: number
  IncrementalTNetLocalCurrency?: number
  IncrementalTNetUSD?: number
  GoalNdpLocalCurrency?: number
  GoalNdpUSD?: number
  GoalNetLocalCurrency?: number
  GoalNetUSD?: number
  GoalTNetLocalCurrency?: number
  GoalTNetUSD?: number
  Owner: string
  BusinessUnit: string
  BusinessUnitTranslation: string
  Period: string
  Currency: string
  ValueType?: string
  /** Bridge participation only; do not use this flag to infer the financial goal type. */
  AddToBridges: 0 | 1
  Status: string
  IsSwot: 0 | 1
  DataInsightRelated: 0 | 1
  SoBGrowth: 0 | 1
  AmplifyImpact: 0 | 1
  Products?: string
  HPInvestmentSum?: number
  HPInvestmentSumUSD?: number
  HPInvestmentSource?: string
  SubCategoryCounts: number
  NetToNdp: number
  UsdToLC: number
  IsCountriesAffected: 0 | 1
  CurrentProgress?: string
  FollowUp?: string
  IsFromGlobalPartner: 0 | 1
  KeyObjectiveCategory: string
  RollupPartnersCount: number
  ActivityType?: string
  KeyObjectiveTags: string
  Justification?: string
  BusinessPriorities?: string
  PracticeBuilder?: string
  Parent: number
  /** Serialized `IDescriptionQuality`, for example `{Score:93,Reasons:[]}`. */
  DescriptionQuality: string
  NameExt: string
}

export interface IFinancialCockpitPartnerKeyInitiatives {
  PartnerId: string
  KeyInitiatives: IFinancialCockpitKeyInitiative[]
}

export interface IFinancialCockpitEltDocuments {
  PartnerId: string
  ELTDocuments: IFinancialCockpitEltDocument[]
}

export interface IFinancialCockpitEltDocument {
  EntryId: number
  Created?: string
  CreatedBy?: string
  Modified?: string
  ModifiedBy?: string
  UID?: string
  FYear: number
  FQuarter: number
  LastStep: number
  Logo: string
  PPTXReport: string
  PartnerId: string | null
  PartnerName: string
  GroupId: number | null
  Picture: string
  GuidelinesWasRead: number
  GuidesStep: number
  FormModified: string
  FormModifiedBy: string
  Version: number
  VersionName: string
  ClonedFromVersion: number
  Finalized: number
  GroupMembers: string
  Settings: string
  EltType: EltType
  Attachment: string
}

export interface IFinancialCockpitQbrDocuments {
  PartnerId: string
  QBRDocuments: IFinancialCockpitQbrDocument[]
}

export interface IFinancialCockpitQbrDocument {
  EntryId: number
  Created: string
  CreatedBy: string
  FQuarter: number | null
  FYear: number | null
  Finalized: number | null
  FormModified: string | null
  FormModifiedBy: string | null
  LastStep: number | null
  Modified: string
  ModifiedBy: string | null
  Name: string | null
  PPTXReport: string | null
  PartnerIDs: string | null
  RTM: PartnerType | null
  Settings: string | null
  IsLegacyDocument: number | null
  DNALoadDate: string | null
  UID: string | null
  GlobalPartnerId: string | null
}

/** The cockpit payload as it arrives on the wire. */
export interface IFinancialCockpitDataApi {
  GeoMapping: IGeoMappingItem[]
  PartnerData: I621Form[]
  PlanningCycles: IFinancialCockpitPlanningCycle[]
  PartnerFinSobData: IPartnerFinSobDataApiGroup[]
  CommitmentData: ICockpitCommitmentData[]
  CompletenessData: ICockpitCompletenessData[]
  CommitmentStatus: ICockpitCommitmentStatus[]
  ActualsAndTargetsSettings: IFinancialCockpitActualsAndTargetsSetting[]
  MDFActivities: IFinancialCockpitPartnerMdfActivities[]
  MDFSales?: ICockpitPartnerMdfSales[]
  KeyInitiatives: IFinancialCockpitPartnerKeyInitiatives[]
  PBMActions: IFinancialCockpitPendingActionsOffering[]
  Users: IFinancialCockpitPartnerUsersOffering[]
  /** One-element array, and absent altogether from a payload that carries no profile. */
  UserInfo?: IFinancialCockpitUserInfo[]
  ELTDocuments?: IFinancialCockpitEltDocuments[]
  QBRDocuments?: IFinancialCockpitQbrDocuments[]
  PartnerFlags?: IP360PartnerFlags[]
  PartnerFlagRequests?: IPartnerFlagRequestsApiGroup[]
  PartnerFlagApprovers?: IPartnerFlagApproverApi[]
  /** Catalog of the attention statuses that can be raised — the same rows for every partner.
   *  Which of them a partner raised is its `CockpitAttentionStatus`, a mask per offering. */
  AttentionDict?: IAttentionStatusApi[]
}

/** What `fetchFinancialCockpitData` resolves to: the wire payload with the Fin/SOB rows and the
 *  MDF activities mapped and the offerings' `ProgramOfferingsJson` parsed. `PartnerData`,
 *  `CommitmentData` and `CommitmentStatus` stay raw — they are fanned straight into the legacy
 *  `commitment`/`configuration` slices, which expect their own DTOs. */
export interface IFinancialCockpitData
  extends Omit<
    IFinancialCockpitDataApi,
    | 'PartnerData'
    | 'UserInfo'
    | 'GeoMapping'
    | 'PartnerFinSobData'
    | 'MDFActivities'
    | 'MDFSales'
    | 'ELTDocuments'
    | 'QBRDocuments'
    | 'PartnerFlagRequests'
    | 'PartnerFlagApprovers'
  > {
  GeoMapping: IMappedGeoMappingItem[]
  UserInfo: IFinancialCockpitUserInfo | null
  PartnerData: ICockpitPartnerData[]
  PartnerFinSobData: IPartnerFinSobRow[]
  MDFActivities: ICockpitPartnerMdfActivities[]
  MDFSales: ICockpitMdfSales[]
  Offerings: ICockpitProgramOffering[]
  PartnerOfferingsCatalog: ICockpitPartnerOfferingsCatalog[]
  AvailableCurrencies: string[]
  ELTDocuments: IFinancialCockpitEltDocuments[]
  QBRDocuments: IFinancialCockpitQbrDocuments[]
  PartnerFlagRequests: IPartnerFlagRequest[]
  PartnerFlagApprovers: string[]
}

export interface IFetchFinancialCockpitDataParams {
  fYear: number
  partnerId?: string
  shouldResetCache?: boolean
  /** Fetched under a board that stays on screen: no download progress is reported, so the page
   *  does not swap the board for its full-screen loader. */
  silent?: boolean
  /** Reloaded after a plan section saved, with the section still open over the board: the
   *  partner data the sections read is not marked as loading, it is replaced on arrival. */
  keepSectionsLoaded?: boolean
  /** Reloaded for another year of the same partner, with the page kept on screen: the open plan
   *  section, the plan deck and the board stay, and their data is replaced on arrival. Fetched
   *  `silent` as well. */
  keepOnScreen?: boolean
}

export interface IFinancialCockpitResponse {
  status: string
  data: IFinancialCockpitDataApi
}

export type { I621Form }
